/* ───────────────────────────────────────────────────────────────────────────
   O16 Return Modal — brand JS (V2 — full-bleed mobile takeover / wide
   editorial desktop panel, frontend only)

   ONE modal, THREE views (form → confirm → success) that crossfade in place
   via a CSS grid stack (see o16-return-modal.css). Rendered once, globally,
   by snippets/o16-return-modal.liquid.

   Opened by ANY [data-o16-return-trigger] element (desktop header nav item +
   burger bottom dock, in both the mobile drawer and the desktop burger
   panel — see header-center-logo.liquid / o16-burger-bottom.liquid).

   OPEN/CLOSE is a class-driven transition on ONE shared
   `.o16-return-modal--open` class (mobile: bottom slide-up echoing the
   burger dock; desktop: scale/opacity), toggled on the host element itself.
   Background scroll is frozen via a DEDICATED `.o16-return-lock` class
   applied to BOTH <html> and <body> (never the burger's own
   `.overflow-hidden-drawer` / `.overflow-hidden-tablet`) — the two coexist
   harmlessly, so opening Return from inside the mobile burger never
   accidentally unlocks the burger's own scroll-lock on close, and close()
   removes ONLY our own class so a still-open burger stays locked.

   Focus-trap pattern matches O16DiscountModal (o16-cart-drawer.js): reuse
   the shared global trapFocus()/removeTrapFocus() (global.js). Because the
   burger bottom dock's "Return" link is ALSO rendered inside the mobile
   hamburger drawer (<header-drawer>, which runs its OWN trapFocus() on
   open), opening this modal from there silently tears down that drawer's
   trap (trapFocus() always removes whatever trap preceded it) — close()
   re-establishes it, exactly like O16DiscountModal.close() restores
   .drawer__inner's trap. The desktop burger panel (<burger-menu>,
   burger-menu.js) never runs a trap of its own, so there is nothing to
   restore there.

   Phase 3 — Submit POSTs the form as JSON to a Netlify function, whose URL
   the owner pastes into Theme settings → O16 Return → "Return form endpoint
   URL" (settings.o16_return_endpoint, rendered onto this custom element as
   `data-o16-return-endpoint` by o16-return-modal.liquid, read once in
   connectedCallback). Leaving that setting blank keeps the ORIGINAL stubbed,
   no-network preview behaviour exactly as before. See `_submitReturn()` /
   `_buildPayload()` / `_buildSuccess()` below for the full contract.
─────────────────────────────────────────────────────────────────────────── */

var O16_RETURN_CROSSFADE_MS = 220;
var O16_RETURN_SUBMIT_DELAY_MS = 600;
var O16_RETURN_CLOSE_FALLBACK_MS = 360;

// Field-name → validator. Returns '' when valid, an error message otherwise.
// `items` is validated separately (see _validateAll) — it's a dynamic array,
// not a single input, so it never goes through this table.
var O16_RETURN_VALIDATORS = {
  full_name: function (value) {
    return value.trim() ? '' : 'Please enter your full name.';
  },
  email: function (value) {
    var v = value.trim();
    if (!v) return 'Please enter your email.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Please enter a valid email address.';
    return '';
  },
  order_number: function (value) {
    return value.trim() ? '' : 'Please enter your order number.';
  }
};

// Single source of truth for the "#"-prefixed order number (V2 Round 8,
// replaces the old client-generated "RTN-…" reference entirely — the
// Success view's reference IS the order number the shopper entered). The
// input's own raw value should already never contain a "#" (see
// _onInput's live strip below), but this strips defensively again so this
// function is safe to call from anywhere, with any raw string, and can
// never produce "##" or "# #" — exactly one "#", or an empty string if
// nothing was ever typed.
function o16ReturnNormalizeOrderNumber(raw) {
  var digits = (raw || '').replace(/#/g, '').trim();
  return digits ? '#' + digits : '';
}

// Human timestamp — the REAL timestamp of record will come from the backend
// once Phase 3 wires this up; this is a client-side display value only.
function o16ReturnFormatTimestamp(date) {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  } catch (e) {
    return date.toString();
  }
}

class O16ReturnModal extends HTMLElement {
  connectedCallback() {
    this._panel = this.querySelector('[data-o16-return-panel]');
    this._main = this.querySelector('.o16-return-modal__main');
    this._form = this.querySelector('[data-o16-return-form]');
    this._views = Array.prototype.slice.call(this.querySelectorAll('[data-o16-return-view]'));
    this._itemsField = this.querySelector('[data-o16-return-items-field]');
    this._itemInput = this.querySelector('[data-o16-return-item-input]');
    this._itemList = this.querySelector('[data-o16-return-item-list]');
    this._items = [];
    this._submitBtn = this.querySelector('[data-o16-return-submit]');
    // Phase 3 — Confirm view's submit-failure error (network/server only —
    // see _submitReturn() below), and the owner-configured Netlify function
    // URL (Theme settings → O16 Return → "Return form endpoint URL",
    // rendered onto this host element by o16-return-modal.liquid). Empty/
    // blank keeps the original stubbed, no-send preview behaviour.
    this._submitErrorEl = this.querySelector('[data-o16-return-submit-error]');
    this._endpoint = (this.dataset.o16ReturnEndpoint || '').trim();
    this._successBody = this.querySelector('[data-o16-return-success-body]');
    // V2 Round 8 — the Success message is four separate block lines (see
    // the file-level V2 ROUND 8 comment in o16-return-modal.liquid),
    // populated individually by _buildSuccess() below.
    this._successEls = {
      confirm: this.querySelector('[data-o16-return-success-confirm]'),
      reference: this.querySelector('[data-o16-return-success-reference]'),
      timestamp: this.querySelector('[data-o16-return-success-timestamp]'),
      next: this.querySelector('[data-o16-return-success-next]')
    };
    // Cached ONCE per page session by _syncDesktopPanelHeight() (V2 Round
    // 8), the first time the Form view is measured — always at initial
    // open(), before any min-height override exists yet, so the
    // measurement is Form's true natural height, never inflated by its own
    // prior floor. null until then.
    this._desktopPanelMinHeight = null;
    this._returnFocus = null;
    this._closing = false;
    // Mobile confirm footer scroll-cue (V2 Round 6 — reuses the cart
    // drawer's own O16ScrollCue visual/behavior, see o16-cart-drawer.js) —
    // see _setupScrollCue / _teardownScrollCue / _updateScrollCue below.
    this._scrollCueEl = null;
    this._scrollCueScroller = null;
    this._scrollCueScrollHandler = null;
    this._scrollCueResizeHandler = null;
    this._scrollCueClickHandler = null;
    // Blur-validation only fires for a field the shopper has actually typed
    // into (see _onInput/_onBlur) — prevents open()'s auto-focused "Full
    // name" from flashing a premature "required" error the instant focus
    // leaves it untouched (e.g. clicking the "Part of my order" radio).
    this._dirtyFields = {};
    this._prefersReducedMotion = !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );

    this._fieldsByName = {};
    var fieldEls = this.querySelectorAll('[data-o16-return-field]');
    for (var i = 0; i < fieldEls.length; i++) {
      var el = fieldEls[i];
      if (el.type === 'radio') continue; // shared "name" across two inputs — read via :checked instead
      this._fieldsByName[el.dataset.o16ReturnField] = el;
    }

    this._errorsByName = {};
    ['full_name', 'email', 'order_number'].forEach((name) => {
      var input = this._fieldsByName[name];
      var errorEl = input ? this.querySelector('#' + input.id + '-error') : null;
      if (errorEl) this._errorsByName[name] = errorEl;
    });
    // No single input backs the items list (it's a dynamic array) — its
    // error element is read directly by id instead of derived from a field.
    this._errorsByName.items = this.querySelector('#O16ReturnItems-error');

    this._summaryEls = {
      name: this.querySelector('[data-o16-return-summary-name]'),
      email: this.querySelector('[data-o16-return-summary-email]'),
      order: this.querySelector('[data-o16-return-summary-order]'),
      scope: this.querySelector('[data-o16-return-summary-scope]'),
      items: this.querySelector('[data-o16-return-summary-items]'),
      commentRow: this.querySelector('[data-o16-return-summary-comment-row]'),
      comment: this.querySelector('[data-o16-return-summary-comment]')
    };

    this._onClick = this._onClick.bind(this);
    this._onKeyup = this._onKeyup.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onFormSubmit = this._onFormSubmit.bind(this);
    this._onItemInputKeydown = this._onItemInputKeydown.bind(this);

    this.addEventListener('click', this._onClick);
    this.addEventListener('keyup', this._onKeyup);
    // blur doesn't bubble — delegate via the capture phase instead.
    this.addEventListener('blur', this._onBlur, true);
    this.addEventListener('input', this._onInput);
    this.addEventListener('change', this._onChange);
    if (this._form) this._form.addEventListener('submit', this._onFormSubmit);
    if (this._itemInput) this._itemInput.addEventListener('keydown', this._onItemInputKeydown);

    this._syncItemsFieldVisibility(false, { skipAnimation: true });
    this._initViewState();
    // V2 Round 5 — mirror the site header's own primary logo into this
    // modal's mobile bar. One-shot + idempotent (see the guard inside): the
    // header is always already in the DOM ahead of this element at initial
    // parse (it renders earlier in <body>), so this never needs to wait for
    // DOMContentLoaded.
    this._mountBarLogo();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('keyup', this._onKeyup);
    this.removeEventListener('blur', this._onBlur, true);
    this.removeEventListener('input', this._onInput);
    this.removeEventListener('change', this._onChange);
    if (this._form) this._form.removeEventListener('submit', this._onFormSubmit);
    if (this._itemInput) this._itemInput.removeEventListener('keydown', this._onItemInputKeydown);
    this._teardownScrollCue();
  }

  // Clones the site header's ACTUAL rendered primary-logo <img> into this
  // modal's mobile bar — see the file-level V2 ROUND 5 comment in
  // o16-return-modal.liquid for why: this snippet renders outside any
  // section's Liquid scope, so `section.settings.logo` (the header's own
  // picker) is unreachable from here (verified empirically). Cloning the
  // already-rendered, already-downloaded node instead means zero duplicated
  // settings and zero extra network cost, and it always mirrors whatever
  // the owner has configured in the customizer. `.shopify-section-header`
  // is Shopify's own auto-applied wrapper class on the header section
  // specifically (declared via that section's schema `"class"` key) — a
  // guaranteed-unique anchor that can never accidentally match
  // footer.liquid's or gift-card.liquid's own same-named `.header__heading-logo`
  // element elsewhere on the page. If no logo is configured at all (a
  // shop-name-only header), `source` is null and this is a no-op — the bar
  // simply stays close-X-only, exactly as before this round.
  _mountBarLogo() {
    var target = this.querySelector('[data-o16-return-bar-logo]');
    if (!target || target.firstElementChild) return; // idempotent — never re-clone
    var source = document.querySelector('.shopify-section-header .header__heading-logo');
    if (!source) return;
    var clone = source.cloneNode(true);
    clone.removeAttribute('id');
    clone.removeAttribute('class');
    clone.setAttribute('loading', 'lazy');
    target.appendChild(clone);
  }

  // Resting-state setup ONLY (no trapFocus — this runs on page load while
  // the modal is still [hidden], and trapFocus() would otherwise hijack the
  // whole page's tab order before the shopper ever opens it).
  _initViewState() {
    this._views.forEach((view) => {
      var isForm = view.dataset.o16ReturnView === 'form';
      view.hidden = !isForm;
      this._setViewControlsDisabled(view, !isForm);
    });
  }

  _setViewControlsDisabled(view, disabled) {
    var controls = view.querySelectorAll('button, input, textarea, select');
    controls.forEach((el) => {
      el.disabled = disabled;
    });
  }

  _getActiveView() {
    for (var i = 0; i < this._views.length; i++) {
      if (!this._views[i].hidden) return this._views[i];
    }
    return null;
  }

  _onClick(event) {
    if (event.target.closest('[data-o16-return-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.target.closest('[data-o16-return-back]')) {
      event.preventDefault();
      this._goToView('form');
      return;
    }
    if (event.target.closest('[data-o16-return-submit]')) {
      event.preventDefault();
      this._submitReturn();
      return;
    }
    if (event.target.closest('[data-o16-return-item-add]')) {
      event.preventDefault();
      this._addItem();
      return;
    }
    var removeItemBtn = event.target.closest('[data-o16-return-item-remove]');
    if (removeItemBtn) {
      event.preventDefault();
      this._removeItem(removeItemBtn.getAttribute('data-o16-return-item-remove'));
      return;
    }
  }

  // Escape closes the modal only.
  _onKeyup(event) {
    if ((event.key === 'Escape' || event.key === 'Esc') && !this.hidden) {
      event.stopPropagation();
      this.close();
    }
  }

  _onChange(event) {
    if (event.target && event.target.name === 'return_scope') {
      this._syncItemsFieldVisibility(event.target.value === 'partial');
    }
  }

  _onBlur(event) {
    var field = event.target;
    if (!field || !field.matches || !field.matches('[data-o16-return-field]')) return;
    var name = field.dataset.o16ReturnField;
    if (name === 'return_scope' || name === 'comment') return; // no blur validation for these
    // Only fields the shopper has actually typed into may blur-validate —
    // guards against open()'s auto-focused "Full name" flashing "required"
    // the instant focus leaves it untouched. _validateAll() (Continue) still
    // marks every field dirty, so a failed submit re-arms blur validation.
    if (!this._dirtyFields[name]) return;
    this._validateField(name);
  }

  _onInput(event) {
    var field = event.target;
    if (!field || !field.matches || !field.matches('[data-o16-return-field]')) return;
    var name = field.dataset.o16ReturnField;
    // V2 Round 8 — a fixed "#" glyph already sits in front of this field
    // (see .__input-prefix); the field's own raw value must never contain
    // one itself (typed OR pasted), so it can never combine with that fixed
    // glyph into "##" or "# #". o16ReturnNormalizeOrderNumber() also strips
    // defensively wherever the value is actually read, belt-and-braces.
    if (name === 'order_number' && field.value.indexOf('#') !== -1) {
      this._stripOrderNumberHash(field);
    }
    if (name === 'full_name' || name === 'email' || name === 'order_number') {
      this._dirtyFields[name] = true;
    }
  }

  // Removes every "#" from the field's live value, preserving cursor
  // position (offset by however many "#" characters were removed BEFORE
  // the cursor, so deleting/typing mid-string never jumps the caret).
  _stripOrderNumberHash(field) {
    var selectionStart = field.selectionStart;
    var hashesBeforeCursor =
      selectionStart != null ? (field.value.slice(0, selectionStart).match(/#/g) || []).length : 0;
    field.value = field.value.replace(/#/g, '');
    if (selectionStart != null && typeof field.setSelectionRange === 'function') {
      var newPos = Math.max(0, selectionStart - hashesBeforeCursor);
      field.setSelectionRange(newPos, newPos);
    }
  }

  _onItemInputKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this._addItem();
    }
  }

  _onFormSubmit(event) {
    event.preventDefault();
    var firstInvalid = this._validateAll();
    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }
    this._buildSummary();
    this._goToView('confirm');
  }

  open(trigger) {
    this._returnFocus = trigger || document.activeElement;
    this.hidden = false;
    // Locks BOTH <html> and <body> — body-only was leaving the background
    // scrollable in some cases (rubber-band / viewport-vs-document mismatch,
    // or <html> being the document's actual scrolling element). body also
    // gets position:fixed (the robust, iOS-proof technique) via the CSS
    // class; `top` is set here to minus the CURRENT scroll position so the
    // page doesn't visually jump when position:fixed takes it out of flow —
    // close() reads this same value back to restore the real scroll
    // position (no jump-to-top on close either). A dedicated class on both
    // elements (never the burger's own .overflow-hidden-drawer /
    // .overflow-hidden-tablet) so the two lock mechanisms coexist
    // harmlessly — see the file-level comment above.
    this._scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = '-' + this._scrollY + 'px';
    document.documentElement.classList.add('o16-return-lock');
    document.body.classList.add('o16-return-lock');
    void this.offsetHeight; // reflow — commits the closed transform/opacity before --open is added, so the transition genuinely animates from it
    requestAnimationFrame(() => {
      this.classList.add('o16-return-modal--open');
    });
    this._goToView('form', { focusFirstField: true });
  }

  close() {
    if (this.hidden || this._closing) return;
    this._closing = true;
    this._teardownScrollCue();

    var activeView = this._getActiveView();
    var wasSuccess = !!(activeView && activeView.dataset.o16ReturnView === 'success');

    var finish = () => {
      this._closing = false;
      this.hidden = true; // set FIRST so any reset below is invisible
      if (wasSuccess) this._resetForm();
      // Removes ONLY our own lock class from both elements — a still-open
      // burger keeps its own overflow-hidden-drawer/-tablet class intact.
      document.documentElement.classList.remove('o16-return-lock');
      document.body.classList.remove('o16-return-lock');
      document.body.style.top = '';

      var focusEl = this._returnFocus;
      var canRefocus = !!(focusEl && document.body.contains(focusEl));

      // Special case: Return was opened from a trigger that lives INSIDE the
      // still-open mobile burger drawer (<header-drawer>) — closing us must
      // re-establish THAT drawer's own trap (a bare removeTrapFocus() would
      // otherwise leave the drawer trap-less). Scoped to whether the ORIGINAL
      // trigger itself is inside the open drawer (not just "some drawer is
      // open somewhere"), so a desktop-header open never mistakenly re-arms
      // an unrelated, merely-still-open mobile drawer.
      var openDrawer = canRefocus ? focusEl.closest('header-drawer details[open]') : null;

      if (openDrawer && typeof trapFocus === 'function') {
        trapFocus(openDrawer, canRefocus ? focusEl : openDrawer);
      } else if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(canRefocus ? focusEl : null);
      }

      // Belt-and-braces: trapFocus()/removeTrapFocus() already focus the
      // target internally, but a reliable return-to-trigger is a hard a11y
      // requirement, not merely a side-effect of a shared helper's
      // implementation — guarantee it explicitly too. preventScroll matches
      // the same fix already established elsewhere in this theme (contact
      // flip-cards) — the explicit window.scrollTo below is the real,
      // final source of truth for scroll position either way.
      if (canRefocus && document.activeElement !== focusEl) {
        focusEl.focus({ preventScroll: true });
      }

      this._returnFocus = null;

      // Scroll restore runs LAST, deliberately — a header trigger that's now
      // off-screen at the restored position would otherwise get pulled back
      // into view by the .focus() calls above (their default scroll-into-view
      // behavior), undoing this. Running it after gives it final say. The
      // forced reflow (offsetHeight read) matters too: without it the layout
      // engine can still be using the just-removed overflow:hidden box on
      // this same tick, which clamps scrollTo to 0. behavior:'instant' is
      // deliberate as well — base.css sets a page-wide scroll-behavior:smooth,
      // which would otherwise animate this into a jarring
      // jump-to-top-then-slide-back-down artifact.
      void document.body.offsetHeight;
      window.scrollTo({ top: this._scrollY || 0, left: 0, behavior: 'instant' });
    };

    this.classList.remove('o16-return-modal--open');

    if (this._prefersReducedMotion || !this._panel) {
      finish();
      return;
    }

    var settled = false;
    var fallback = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      this._panel.removeEventListener('transitionend', onTransitionEnd);
      finish();
    }, O16_RETURN_CLOSE_FALLBACK_MS);

    var onTransitionEnd = (event) => {
      if (event.target !== this._panel || settled) return;
      settled = true;
      window.clearTimeout(fallback);
      this._panel.removeEventListener('transitionend', onTransitionEnd);
      finish();
    };
    this._panel.addEventListener('transitionend', onTransitionEnd);
  }

  // options.focusFirstField — true only on the initial open() (spec: "on
  // open, focus the first field"); every subsequent view change instead
  // moves focus to the new view's heading.
  _goToView(name, options) {
    options = options || {};
    var target = this._views.find((v) => v.dataset.o16ReturnView === name);
    if (!target) return;
    var current = this._getActiveView();

    // Desktop confirm layout switch (V2 Round 3) — self-heals on every view
    // change; hides the constant founder image and reshapes __main into the
    // process/summary 2-column split, CONFIRM view only. See the
    // `.o16-return-modal--confirm` rules in o16-return-modal.css.
    this.classList.toggle('o16-return-modal--confirm', name === 'confirm');
    // Desktop form layout switch (V2 Round 4) — same self-healing pattern:
    // swaps the founder image to the LEFT / form to the RIGHT, FORM view
    // only (Success keeps the original image-right layout). See the
    // `.o16-return-modal--form` rules in o16-return-modal.css.
    this.classList.toggle('o16-return-modal--form', name === 'form');

    this._views.forEach((view) => {
      this._setViewControlsDisabled(view, view !== target);
    });

    if (current !== target) {
      if (current && !this._prefersReducedMotion) {
        this._crossfade(current, target);
      } else {
        if (current) current.hidden = true;
        target.hidden = false;
      }
    }

    // Mobile confirm scroll-cue (V2 Round 5) — self-heals on every view
    // change, same pattern as the two host-class toggles above: armed only
    // while Confirm is the active view, torn down (observer disconnected,
    // resize listener removed) the instant we navigate anywhere else.
    // target.hidden is already false by this point in both branches above,
    // so the layout read inside _setupScrollCue is measuring real, visible
    // content, not a [hidden] box.
    if (name === 'confirm') {
      this._setupScrollCue();
    } else {
      this._teardownScrollCue();
    }

    var heading = target.querySelector('.o16-return-modal__title');
    if (heading && this._panel) {
      this._panel.setAttribute('aria-labelledby', heading.id);
    }

    var focusTarget = options.focusFirstField
      ? target.querySelector('[data-o16-return-field="full_name"]') || heading
      : heading;

    // Synchronous — doesn't need to wait for the crossfade animation, since
    // the enabled/disabled state above (which getFocusableElements reads)
    // is already final by this point.
    if (typeof trapFocus === 'function' && this._panel) {
      trapFocus(this._panel, focusTarget || this._panel);
    } else if (focusTarget) {
      focusTarget.focus();
    }

    // V2 Round 8 — self-heals on every view change, same pattern as the
    // host-class toggles above: on desktop, keeps `.__main` no shorter than
    // Form's own natural height (measured once, see the method below), so
    // Confirm/Success never render shorter than Form. `target.hidden` is
    // already false by this point in every branch above (crossfade sets it
    // synchronously; the reduced-motion branch sets it directly), so this
    // is always measuring real, visible layout.
    this._syncDesktopPanelHeight();
  }

  // Desktop only: floors `.__main`'s height at Form's own natural (content-
  // driven) height, so Confirm/Success — whose own content is shorter —
  // never collapse the panel down from whatever height Form established
  // (owner: "define it by the image height" — Form is the view the
  // constant side image was always sized against). Measured with
  // `offsetHeight` (immune to the panel's own open/close scale transform,
  // unlike getBoundingClientRect) the FIRST time Form is the active view —
  // always true at initial open(), before this method has ever applied a
  // min-height override yet, so the reading is Form's true, unconstrained
  // height, never inflated by its own prior floor. Cached for the rest of
  // the page session; mobile always clears the inline override (mobile's
  // own `.__main` sizing is unrelated — see the base flex rule in
  // o16-return-modal.css).
  _syncDesktopPanelHeight() {
    if (!this._main) return;
    var isDesktop = window.matchMedia && window.matchMedia('(min-width: 750px)').matches;
    if (!isDesktop) {
      this._main.style.minHeight = '';
      return;
    }
    if (this._desktopPanelMinHeight == null) {
      var current = this._getActiveView();
      if (current && current.dataset.o16ReturnView === 'form') {
        var measured = current.offsetHeight;
        if (measured > 0) this._desktopPanelMinHeight = measured;
      }
    }
    if (this._desktopPanelMinHeight) {
      this._main.style.minHeight = this._desktopPanelMinHeight + 'px';
    }
  }

  _crossfade(fromEl, toEl) {
    toEl.hidden = false;
    toEl.style.opacity = '0';
    void toEl.offsetHeight; // force reflow so the transition below animates from 0
    requestAnimationFrame(() => {
      fromEl.classList.add('o16-return-modal__view--leaving');
      toEl.style.opacity = '';
      toEl.classList.add('o16-return-modal__view--entering');
    });
    window.setTimeout(() => {
      fromEl.hidden = true;
      fromEl.classList.remove('o16-return-modal__view--leaving');
      toEl.classList.remove('o16-return-modal__view--entering');
    }, O16_RETURN_CROSSFADE_MS);
  }

  // ── MOBILE CONFIRM FOOTER SCROLL-CUE (V2 Round 6) ────────────────────────
  // Submit return / Go back are now pinned in `.__view-footer`, always
  // visible without scrolling (see the no-scroll-shell CSS) — this cue only
  // signals that the content ABOVE it (`.__confirm-body`: summary +
  // process, the view's own scroll region) has more to see. Mirrors the
  // cart drawer's own O16ScrollCue `_update()` exactly (o16-cart-drawer.js):
  // visible while the scroller overflows and isn't scrolled to the bottom;
  // click scrolls it down by ~70% of its own height.
  _setupScrollCue() {
    this._teardownScrollCue();
    var cue = this.querySelector('[data-o16-return-footer-scroll-cue]');
    var scroller = this.querySelector('[data-o16-return-confirm-body]');
    if (!cue || !scroller) return;
    this._scrollCueEl = cue;
    this._scrollCueScroller = scroller;

    var recompute = () => this._updateScrollCue();
    // Two rAFs: the crossfade/`hidden` toggle above and any font/layout
    // settling both need a real paint before scrollHeight is trustworthy.
    window.requestAnimationFrame(() => window.requestAnimationFrame(recompute));

    this._scrollCueScrollHandler = recompute;
    scroller.addEventListener('scroll', this._scrollCueScrollHandler, { passive: true });

    // Re-checks "is this even scrollable" if the shopper resizes mid-session
    // (e.g. rotates, or the modal happens to stay open across a breakpoint
    // change) — cheap and rare enough to skip debouncing.
    this._scrollCueResizeHandler = recompute;
    window.addEventListener('resize', this._scrollCueResizeHandler);

    this._scrollCueClickHandler = () => {
      scroller.scrollBy({
        top: scroller.clientHeight * 0.7,
        behavior: this._prefersReducedMotion ? 'auto' : 'smooth'
      });
    };
    cue.addEventListener('click', this._scrollCueClickHandler);
  }

  _teardownScrollCue() {
    if (this._scrollCueScroller && this._scrollCueScrollHandler) {
      this._scrollCueScroller.removeEventListener('scroll', this._scrollCueScrollHandler);
    }
    if (this._scrollCueResizeHandler) {
      window.removeEventListener('resize', this._scrollCueResizeHandler);
      this._scrollCueResizeHandler = null;
    }
    if (this._scrollCueEl && this._scrollCueClickHandler) {
      this._scrollCueEl.removeEventListener('click', this._scrollCueClickHandler);
    }
    if (this._scrollCueEl) {
      this._scrollCueEl.classList.remove('is-visible');
    }
    this._scrollCueEl = null;
    this._scrollCueScroller = null;
    this._scrollCueScrollHandler = null;
    this._scrollCueClickHandler = null;
  }

  _updateScrollCue() {
    var cue = this._scrollCueEl;
    var scroller = this._scrollCueScroller;
    if (!cue || !scroller) return;
    var mobile = window.matchMedia && window.matchMedia('(max-width: 749.98px)').matches;
    if (!mobile) {
      cue.classList.remove('is-visible');
      return;
    }
    var overflow = scroller.scrollHeight - scroller.clientHeight;
    var atBottom = scroller.scrollTop >= overflow - 8;
    cue.classList.toggle('is-visible', overflow > 8 && !atBottom);
  }

  _syncItemsFieldVisibility(open, opts) {
    opts = opts || {};
    var field = this._itemsField;
    if (!field) return;

    if (open) {
      field.hidden = false;
      if (opts.skipAnimation || this._prefersReducedMotion) {
        field.classList.add('is-open');
      } else {
        void field.offsetHeight;
        requestAnimationFrame(() => field.classList.add('is-open'));
      }
    } else {
      // Switching back to "My entire order" (or a full form reset) clears
      // whatever partial-return items were staged — same convention as the
      // old single free-text input clearing its value on close.
      this._items = [];
      this._renderItems();
      this._clearFieldError('items');
      if (this._itemInput) this._itemInput.value = '';
      field.classList.remove('is-open');
      if (opts.skipAnimation || this._prefersReducedMotion) {
        field.hidden = true;
      } else {
        window.setTimeout(() => {
          if (!field.classList.contains('is-open')) field.hidden = true;
        }, O16_RETURN_CROSSFADE_MS);
      }
    }
  }

  // ADD a single item to the partial-return list — duplicate-guarded
  // (case-insensitive), same convention as O16DiscountModal's code list.
  _addItem() {
    if (!this._itemInput) return;
    var value = this._itemInput.value.trim();
    if (!value) return;
    if (!this._items.some((item) => item.toLowerCase() === value.toLowerCase())) {
      this._items = this._items.concat([value]);
      this._renderItems();
    }
    this._itemInput.value = '';
    this._itemInput.focus();
    this._clearFieldError('items');
  }

  _removeItem(value) {
    this._items = this._items.filter((item) => item !== value);
    this._renderItems();
    if (this._itemInput) this._itemInput.focus();
  }

  // Full rebuild every call — the list stays short (a shopper's own return),
  // never a hot path. textContent only (never innerHTML) for the item name.
  _renderItems() {
    if (!this._itemList) return;
    this._itemList.innerHTML = '';
    this._items.forEach((item) => {
      var li = document.createElement('li');
      li.className = 'o16-return-modal__item-list-item';

      var nameEl = document.createElement('span');
      nameEl.className = 'o16-return-modal__item-name';
      nameEl.textContent = item;

      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'o16-return-modal__item-remove';
      removeBtn.setAttribute('data-o16-return-item-remove', item);
      removeBtn.setAttribute('aria-label', 'Remove ' + item);
      removeBtn.textContent = 'Remove';

      li.appendChild(nameEl);
      li.appendChild(removeBtn);
      this._itemList.appendChild(li);
    });
  }

  _validateField(name) {
    var field = this._fieldsByName[name];
    if (!field) return true;
    var validator = O16_RETURN_VALIDATORS[name];
    var message = validator ? validator(field.value, this._form) : '';
    var errorEl = this._errorsByName[name];

    if (message) {
      field.setAttribute('aria-invalid', 'true');
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      return false;
    }

    field.removeAttribute('aria-invalid');
    this._clearFieldError(name);
    return true;
  }

  _clearFieldError(name) {
    var errorEl = this._errorsByName[name];
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
    var field = this._fieldsByName[name];
    if (field) field.removeAttribute('aria-invalid');
  }

  _validateAll() {
    var order = ['full_name', 'email', 'order_number'];
    // Continue/submit always validates every field regardless of dirty
    // state — mark all three dirty here too, so if this fails, blur
    // validation is now armed and re-checks/clears live as the shopper
    // fixes each one (see _onBlur's dirty gate).
    order.forEach((name) => {
      this._dirtyFields[name] = true;
    });
    var firstInvalid = null;
    order.forEach((name) => {
      var ok = this._validateField(name);
      if (!ok && !firstInvalid) firstInvalid = this._fieldsByName[name];
    });

    // Items is a dynamic array, not a single input — validated separately:
    // required only once "Part of my order" is selected.
    var scopeInput = this._form.querySelector('input[name="return_scope"]:checked');
    var scope = scopeInput ? scopeInput.value : 'entire';
    var itemsErrorEl = this._errorsByName.items;
    if (scope === 'partial' && this._items.length === 0) {
      if (itemsErrorEl) {
        itemsErrorEl.textContent = "Please add at least one item you'd like to return.";
        itemsErrorEl.hidden = false;
      }
      if (!firstInvalid) firstInvalid = this._itemInput;
    } else {
      this._clearFieldError('items');
    }

    return firstInvalid;
  }

  _clearAllErrors() {
    Object.keys(this._errorsByName).forEach((name) => this._clearFieldError(name));
  }

  _buildSummary() {
    // Every arrival at Confirm (Continue from Form, or a re-Continue after
    // "Go back") starts from a clean slate — any submit error left over
    // from a previous failed attempt is stale by now.
    this._clearSubmitError();

    var name = (this._fieldsByName.full_name.value || '').trim();
    var email = (this._fieldsByName.email.value || '').trim();
    // V2 Round 8 — the single authoritative order number: always exactly
    // one "#" + whatever digits the shopper entered (see
    // o16ReturnNormalizeOrderNumber above). This is also what the Success
    // view's "Reference:" line reuses — see _buildSuccess() below; the
    // random client-generated "RTN-…" reference is retired entirely.
    var order = o16ReturnNormalizeOrderNumber(this._fieldsByName.order_number.value);
    var scopeInput = this._form.querySelector('input[name="return_scope"]:checked');
    var scope = scopeInput ? scopeInput.value : 'entire';
    var comment = (this._fieldsByName.comment.value || '').trim();

    if (this._summaryEls.name) this._summaryEls.name.textContent = name;
    if (this._summaryEls.email) this._summaryEls.email.textContent = email;
    if (this._summaryEls.order) this._summaryEls.order.textContent = order;
    if (this._summaryEls.scope) {
      this._summaryEls.scope.textContent = scope === 'partial' ? 'Part of my order' : 'My entire order';
    }

    if (this._summaryEls.items) {
      this._summaryEls.items.innerHTML = '';
      if (scope === 'partial' && this._items.length) {
        this._items.forEach((item) => {
          var li = document.createElement('li');
          li.className = 'o16-return-modal__summary-item';
          li.textContent = item;
          this._summaryEls.items.appendChild(li);
        });
        this._summaryEls.items.hidden = false;
      } else {
        this._summaryEls.items.hidden = true;
      }
    }

    if (this._summaryEls.commentRow && this._summaryEls.comment) {
      if (comment) {
        this._summaryEls.commentRow.hidden = false;
        this._summaryEls.comment.textContent = comment;
      } else {
        this._summaryEls.commentRow.hidden = true;
        this._summaryEls.comment.textContent = '';
      }
    }

    this._lastEmail = email;
    this._lastOrderNumber = order;
  }

  // Phase 3 — the exact JSON contract the Netlify function expects. Read
  // fresh off the live fields at submit time (not the Confirm summary's
  // cached _lastEmail/_lastOrderNumber, which only exist to drive
  // _buildSuccess()'s stub fallback) — Confirm never lets the shopper edit
  // these, so a fresh read and the summary always agree. `company` is the
  // honeypot (see .o16-return-modal__honeypot in the Form view) — empty for
  // every real shopper, only ever non-empty when a bot filled it in.
  _buildPayload() {
    var name = (this._fieldsByName.full_name ? this._fieldsByName.full_name.value : '').trim();
    var email = (this._fieldsByName.email ? this._fieldsByName.email.value : '').trim();
    var orderNumber = o16ReturnNormalizeOrderNumber(
      this._fieldsByName.order_number ? this._fieldsByName.order_number.value : ''
    );
    var scopeInput = this._form.querySelector('input[name="return_scope"]:checked');
    var scope = scopeInput ? scopeInput.value : 'entire';
    var comment = (this._fieldsByName.comment ? this._fieldsByName.comment.value : '').trim();
    var company = (this._fieldsByName.company ? this._fieldsByName.company.value : '').trim();

    return {
      name: name,
      email: email,
      orderNumber: orderNumber,
      type: scope, // 'entire' | 'partial'
      items: scope === 'partial' ? this._items.slice() : [],
      comment: comment,
      company: company
    };
  }

  _showSubmitError() {
    if (!this._submitErrorEl) return;
    this._submitErrorEl.textContent = 'Something went wrong — please try again, or email support@o16.dk.';
    this._submitErrorEl.hidden = false;
  }

  _clearSubmitError() {
    if (!this._submitErrorEl) return;
    this._submitErrorEl.textContent = '';
    this._submitErrorEl.hidden = true;
  }

  // Phase 3 — POSTs the payload to the owner-configured Netlify function
  // (settings.o16_return_endpoint, read once in connectedCallback) and only
  // advances to Success on a genuine server confirmation. Two modes:
  //
  //   NO ENDPOINT CONFIGURED (this._endpoint is empty — the owner hasn't
  //   pasted the function URL into the theme editor yet): preserves the
  //   ORIGINAL stubbed behaviour exactly — a short simulated delay, a
  //   client-generated reference (the order number) + client timestamp,
  //   straight to Success. This keeps the modal fully demoable before the
  //   backend is deployed and never breaks in preview.
  //
  //   ENDPOINT CONFIGURED: a real fetch(). On HTTP 200 with a JSON body of
  //   { ok:true, reference, submittedAt }, Success uses THOSE
  //   server-authoritative values (falling back to the order number /
  //   client time only if the response omits them — see _buildSuccess()).
  //   On any non-2xx response, a malformed body, or a network error, Success
  //   is never shown: the button re-enables, an inline error appears next to
  //   it, and every entered value is left exactly as the shopper typed it so
  //   they can simply press Submit again.
  _submitReturn() {
    var btn = this._submitBtn;
    if (!btn || btn.classList.contains('is-loading')) return;

    this._clearSubmitError();
    var payload = this._buildPayload();

    btn.classList.add('is-loading');
    btn.setAttribute('aria-disabled', 'true');
    var originalHTML = btn.innerHTML;
    btn.innerHTML =
      '<span class="o16-return-modal__spinner" aria-hidden="true"></span>' +
      '<span class="visually-hidden">Submitting…</span>';

    var restoreButton = () => {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-disabled');
      btn.innerHTML = originalHTML;
    };

    if (!this._endpoint) {
      window.setTimeout(() => {
        restoreButton();
        this._buildSuccess(payload.orderNumber, null);
        this._goToView('success');
      }, O16_RETURN_SUBMIT_DELAY_MS);
      return;
    }

    fetch(this._endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((response) => {
        return response
          .json()
          .catch(() => null) // non-JSON / empty body — treated as a failure below
          .then((data) => ({ httpOk: response.ok, data: data }));
      })
      .then((result) => {
        if (!result.httpOk || !result.data || result.data.ok !== true) {
          throw new Error('o16-return-submit-failed');
        }
        restoreButton();
        this._buildSuccess(payload.orderNumber, result.data);
        this._goToView('success');
      })
      .catch(() => {
        restoreButton();
        this._showSubmitError();
      });
  }

  // V2 Round 8 — four separate lines (owner-specified spacing lives in
  // o16-return-modal.css's `.__success-line--*` rules, not here): sentence 1
  // → reference → timestamp → next-steps sentence.
  //
  // Phase 3 — `fallbackOrderNumber` is the order number computed fresh by
  // _submitReturn()'s _buildPayload() (always passed by both call sites
  // below); `responseData` is the Netlify function's parsed JSON body
  // ({ ok:true, reference, submittedAt }) when a real POST succeeded, or
  // null in stub/no-endpoint mode. Server-authoritative values win whenever
  // present; a missing/invalid field on either side falls back to the order
  // number / a fresh client timestamp — Success can never render blank.
  _buildSuccess(fallbackOrderNumber, responseData) {
    if (!this._successBody) return;
    var email = this._lastEmail || (this._fieldsByName.email ? this._fieldsByName.email.value.trim() : '');
    var orderNumber =
      fallbackOrderNumber ||
      this._lastOrderNumber ||
      o16ReturnNormalizeOrderNumber(this._fieldsByName.order_number ? this._fieldsByName.order_number.value : '');

    var reference = (responseData && responseData.reference) || orderNumber;

    var submittedDate = responseData && responseData.submittedAt ? new Date(responseData.submittedAt) : null;
    if (!submittedDate || isNaN(submittedDate.getTime())) submittedDate = new Date();
    var timestamp = o16ReturnFormatTimestamp(submittedDate);

    if (this._successEls.confirm) {
      this._successEls.confirm.textContent = "We've emailed a confirmation to " + email + '.';
    }
    if (this._successEls.reference) {
      this._successEls.reference.textContent = 'Reference: ' + reference;
    }
    if (this._successEls.timestamp) {
      this._successEls.timestamp.textContent = timestamp;
    }
    if (this._successEls.next) {
      this._successEls.next.textContent = "We'll be in touch with the next steps.";
    }
  }

  // Clears every entered value + returns internal state to the Form view —
  // called once the shopper dismisses the Success view (any close path),
  // so a fresh open() always starts clean. Closing from Form/Confirm instead
  // PRESERVES entries (no reset), matching "Go back" already preserving them.
  _resetForm() {
    if (this._form) this._form.reset();
    this._clearAllErrors();
    this._clearSubmitError();
    this._syncItemsFieldVisibility(false, { skipAnimation: true });
    this._dirtyFields = {};
    // Belt-and-braces — _goToView('form', ...) below already sets these via
    // its own self-healing toggles, but a fresh open() must never inherit a
    // stale desktop confirm/form-layout state from the previous session.
    this.classList.remove('o16-return-modal--confirm');
    this.classList.remove('o16-return-modal--form');

    this._views.forEach((view) => {
      var isForm = view.dataset.o16ReturnView === 'form';
      view.hidden = !isForm;
      view.classList.remove('o16-return-modal__view--leaving', 'o16-return-modal__view--entering');
      view.style.opacity = '';
      this._setViewControlsDisabled(view, !isForm);
    });
  }
}

if (!customElements.get('o16-return-modal')) {
  customElements.define('o16-return-modal', O16ReturnModal);
}

// Trigger delegation from document — the trigger buttons live in the header
// / burger dock, outside this custom element's own DOM, and (for the burger
// dock) can be recreated by other components, so a document-level listener
// (rather than a one-shot DOMContentLoaded query) is the only version that
// never goes stale. Matches the discount-modal trigger's own delegation
// pattern in o16-cart-drawer.js.
//
// CAPTURE phase + stopPropagation() is deliberate, not stylistic: the burger
// dock's trigger (o16-burger-bottom.liquid) is ALSO rendered inside the
// mobile hamburger drawer (<header-drawer>, global.js MenuDrawer), whose own
// constructor wires EVERY <button> inside it (querySelectorAll('button'),
// with only a few unrelated exclusions) to onCloseButtonClick — a generic
// "close the nearest <details>" handler that anchors were never subject to.
// Now that this trigger is a real <button> (required for correct semantics —
// see header-center-logo.liquid), it would otherwise ALSO collapse the
// mobile drawer out from under our modal the instant it's tapped. Listening
// on the capture phase intercepts the click before it ever reaches that
// bubble-phase, button-bound listener; stopPropagation() there prevents it
// from running at all. Scoped narrowly to clicks that actually target a
// [data-o16-return-trigger] element — every other click on the page is
// completely unaffected.
document.addEventListener(
  'click',
  (event) => {
    var trigger = event.target.closest('[data-o16-return-trigger]');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    var modal = document.querySelector('o16-return-modal');
    if (modal && typeof modal.open === 'function') modal.open(trigger);
  },
  true
);
